import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useFriends } from '../context/FriendContext';
import { getLevelInfo, getGrade } from '../utils/xpUtils';
import { F1_DRIVERS, driverImgUrl } from '../constants/f1Drivers';
import Icon from '../components/Icon';
import ChatModal from '../components/ChatModal';
import ChallengeModal from '../components/ChallengeModal';

const levelColors = ['#a1a1aa','#84cc16','#facc15','#fb923c','#f97316','#ef4444','#e10600','#dc2626','#b91c1c','#991b1b'];
const levelColor = (lvl) => levelColors[Math.min(lvl, levelColors.length - 1)];

const XPBar = ({ xp }) => {
  const { level, currentXP, nextLevelXP, progress } = getLevelInfo(xp ?? 0);
  const grade = getGrade(level);
  const [displayed, setDisplayed] = React.useState(0);
  React.useEffect(() => { const t = setTimeout(() => setDisplayed(progress), 200); return () => clearTimeout(t); }, [progress]);
  const color = levelColor(level);
  return (
    <div className="card mb-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center font-black text-sm border-2"
            style={{ borderColor: color, color, backgroundColor: `${color}18` }}>{level}</div>
          <div>
            <p className="text-white font-bold text-sm">Niveau {level}</p>
            <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded"
              style={{ color: grade.color, background: grade.bg }}>{grade.label}</span>
          </div>
        </div>
        <div className="text-right">
          <p className="text-zinc-400 text-xs">XP total</p>
          <p className="text-white font-black">{(xp ?? 0).toLocaleString()}</p>
        </div>
      </div>
      <div className="w-full h-3 bg-zinc-800 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-1000 ease-out"
          style={{ width: `${Math.min(displayed * 100, 100)}%`, background: `linear-gradient(90deg, ${color}99, ${color})` }} />
      </div>
      <div className="flex justify-between mt-1.5">
        <span className="text-[10px] text-zinc-600">Niv. {level}</span>
        <span className="text-[10px] text-zinc-600">Niv. {level + 1} — encore {(nextLevelXP - currentXP).toLocaleString()} XP</span>
      </div>
    </div>
  );
};

const BREAKDOWN_LABELS = {
  winner: 'Vainqueur', p2: 'P2', p3: 'P3',
  fastestLap: 'Meilleur tour', polePosition: 'Pole',
  fastestQualifyingLap: 'Meilleur tour qualif',
  q1Eliminated: 'Q1 éliminés', q2Eliminated: 'Q2 éliminés',
  safetyCar: 'Safety Car', virtualSafetyCar: 'Virtual SC', redFlag: 'Drapeau rouge',
};

const PronosticCard = ({ pronostic }) => {
  const { race, picks, score, scoreBreakdown, sprintPicks, sprintScore } = pronostic;
  const [expanded, setExpanded] = useState(false);
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : '—';
  const isLocked = race?.isLocked || (race?.deadline && new Date() > new Date(race.deadline));

  return (
    <div className="card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-xs text-zinc-500">Round {race?.round} · {race?.season}</span>
            {isLocked && race?.results?.winner
              ? <span className="text-xs bg-zinc-700 text-zinc-300 px-2 py-0.5 rounded-full">Terminé</span>
              : isLocked
              ? <span className="text-xs bg-orange-900/30 text-orange-400 border border-orange-800 px-2 py-0.5 rounded-full">Verrouillé</span>
              : <span className="text-xs bg-f1red/10 text-f1red border border-f1red/30 px-2 py-0.5 rounded-full">En cours</span>
            }
          </div>
          <h3 className="font-bold text-white text-base truncate">{race?.raceName || '—'}</h3>
          <p className="text-zinc-500 text-xs mt-0.5">{race?.circuit?.country} · {formatDate(race?.sessions?.race?.date)}</p>
        </div>
        <div className="shrink-0 text-right">
          {score != null
            ? <span className="text-2xl font-black text-f1red">{score} pts</span>
            : <span className="text-zinc-500 text-sm">En attente</span>
          }
        </div>
      </div>

      <button type="button" onClick={() => setExpanded(e => !e)}
        className="mt-3 text-xs text-zinc-500 hover:text-zinc-300 flex items-center gap-1">
        <Icon name={expanded ? 'chevronUp' : 'chevronDown'} size={13} />
        {expanded ? 'Masquer' : 'Voir les picks'}
      </button>

      {expanded && picks && (
        <div className="mt-3 pt-3 border-t border-zinc-800 flex flex-col gap-1.5">
          {Object.entries(BREAKDOWN_LABELS).map(([key, label]) => {
            const val = picks[key];
            const isBool = typeof val === 'boolean';
            const isArr = Array.isArray(val);
            const hasVal = isBool ? true : isArr ? val.length > 0 : !!val;
            if (!hasVal) return null;
            const resultVal = race?.results?.[key];
            const hasResult = resultVal != null && (isArr ? Array.isArray(resultVal) && resultVal.length > 0 : resultVal !== '');
            const normStr = (v) => v ? String(v).toUpperCase().trim() : v;
            const arrMatches = isArr ? (val || []).filter(d => (resultVal || []).some(r => normStr(r) === normStr(d))) : [];
            const isCorrect = hasResult && (isArr ? arrMatches.length > 0 : isBool ? val === resultVal : normStr(val) === normStr(resultVal));
            const displayPick = isBool ? (val ? 'Oui' : 'Non') : isArr ? val.map(d => String(d).toUpperCase()).join(', ') : String(val || '').toUpperCase();
            const pts = scoreBreakdown?.[key];
            return (
              <div key={key} className="flex items-start justify-between gap-2 py-0.5">
                <span className="text-zinc-500 text-xs shrink-0">{label}</span>
                <div className="flex items-center gap-1.5 flex-wrap justify-end">
                  <span className={`text-xs font-medium text-right ${
                    !hasResult ? 'text-white' : isCorrect ? 'text-green-400' : 'text-red-400'
                  }`}>{displayPick}</span>
                  {hasResult && !isArr && !isCorrect && resultVal && (
                    <><span className="text-zinc-600 text-[10px]">→</span>
                    <span className="text-green-400 text-xs font-medium">{typeof resultVal === 'boolean' ? (resultVal ? 'Oui' : 'Non') : String(resultVal).toUpperCase()}</span></>
                  )}
                  {pts != null && <span className={`text-[10px] font-black px-1.5 py-0.5 rounded ${pts >= 0 ? 'text-green-400 bg-green-900/20' : 'text-red-400 bg-red-900/20'}`}>{pts >= 0 ? `+${pts}` : pts}</span>}
                </div>
              </div>
            );
          })}
          {sprintPicks?.winner && (
            <div className="mt-2 pt-2 border-t border-zinc-800/60">
              <p className="text-[10px] text-orange-400 uppercase tracking-widest mb-1.5 flex items-center gap-1">
                <Icon name="lightning" size={10} /> Sprint
                {sprintScore != null && <span className="text-orange-400 font-black ml-auto">{sprintScore} pts</span>}
              </p>
              {[['Vainqueur','winner'],['P2','p2'],['P3','p3']].map(([lbl, k]) => {
                const v = sprintPicks[k]; if (!v) return null;
                const rv = race?.sprintResults?.[k];
                const ok = !!rv && String(v || '').toUpperCase() === String(rv || '').toUpperCase();
                return (
                  <div key={k} className="flex items-start justify-between gap-2 py-0.5">
                    <span className="text-zinc-500 text-xs">{lbl}</span>
                    <span className={`text-xs font-medium ${!rv ? 'text-white' : ok ? 'text-green-400' : 'text-red-400'}`}>{v}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const PlayerProfile = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { isFriend, hasPendingFrom, sendRequest, removeFriend, fetchFriends } = useFriends() || {};
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [friendStatus, setFriendStatus] = useState('none');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState('');
  const [chatOpen, setChatOpen] = useState(false);
  const [challengeOpen, setChallengeOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/users/${id}`)
      .then(r => setProfile(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!user || !profile) return;
    if (user.id === profile._id || String(user.id) === String(profile._id)) { setFriendStatus('self'); return; }
    if (isFriend?.(profile._id)) { setFriendStatus('friend'); return; }
    if (hasPendingFrom?.(profile._id)) { setFriendStatus('pending_incoming'); return; }
    setFriendStatus('none');
  }, [user, profile, isFriend, hasPendingFrom]);

  const handleAddFriend = async () => {
    setActionLoading(true);
    try {
      await sendRequest?.(profile._id);
      setFriendStatus('pending_sent');
      setActionMsg('Invitation envoyée !');
    } catch (e) {
      setActionMsg(e?.response?.data?.message || 'Erreur');
    } finally { setActionLoading(false); }
  };

  const handleRemoveFriend = async () => {
    setActionLoading(true);
    try {
      await removeFriend?.(profile._id);
      setFriendStatus('none');
      fetchFriends?.();
    } catch {} finally { setActionLoading(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-zinc-400">Chargement...</div>;
  if (!profile) return <div className="flex items-center justify-center h-64 text-zinc-400">Profil introuvable.</div>;

  const seasonDriverObj = profile.seasonDriver
    ? F1_DRIVERS.find(d => d.code === profile.seasonDriver.code || d.name === profile.seasonDriver.name) || null
    : null;
  const driverObj = seasonDriverObj || (profile.favoriteDriver
    ? F1_DRIVERS.find(d => d.name === profile.favoriteDriver.name) || null
    : null);
  const pronostics = profile.pronostics || [];
  const scored = pronostics.filter(p => p.score != null);
  const pronosticScore = profile.pronosticScore ?? pronostics.reduce((a, p) => a + (p.score || 0), 0);
  const seasonDriverPoints = profile.seasonDriverPoints || 0;
  const avg = scored.length > 0 ? Math.round(pronosticScore / scored.length) : null;

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <Link to="/joueurs" className="text-zinc-500 text-sm hover:text-zinc-300 flex items-center gap-1 mb-6">
        <Icon name="chevronDown" size={12} className="rotate-90" /> Joueurs
      </Link>

      {/* Header card */}
      <div className="card mb-4">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-full overflow-hidden border-2 shrink-0"
            style={{ borderColor: driverObj ? `${driverObj.color}66` : '#27272a' }}>
            {profile.avatar
              ? <img src={profile.avatar} alt={profile.username} className="w-full h-full object-cover" />
              : <div className="w-full h-full bg-f1red/20 flex items-center justify-center text-2xl font-black text-f1red">
                  {profile.username?.[0]?.toUpperCase()}
                </div>
            }
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h1 className="text-2xl font-black text-white">{profile.username}</h1>
                <p className="text-zinc-500 text-xs mt-0.5">
                  Membre depuis {new Date(profile.createdAt).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                </p>
              </div>
              {user && friendStatus !== 'self' && (
                <div className="shrink-0">
                  {friendStatus === 'friend'
                    ? <button onClick={handleRemoveFriend} disabled={actionLoading} className="btn-secondary text-xs py-1.5 px-3">Amis ✓</button>
                    : friendStatus === 'pending_sent'
                    ? <span className="text-xs text-zinc-500 border border-zinc-800 rounded-lg px-3 py-1.5">Invitation envoyée</span>
                    : friendStatus === 'pending_incoming'
                    ? <span className="text-xs text-zinc-400 border border-zinc-700 rounded-lg px-3 py-1.5">Invitation reçue</span>
                    : <button onClick={handleAddFriend} disabled={actionLoading} className="btn-primary text-xs py-1.5 px-3">+ Ajouter</button>
                  }
                </div>
              )}
            </div>
            {actionMsg && <p className="text-green-400 text-xs mt-2">{actionMsg}</p>}
            {friendStatus === 'friend' && (
              <div className="flex gap-2 mt-3">
                <button onClick={() => setChatOpen(true)}
                  className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5"><Icon name="message" size={12} /> Message</button>
                <button onClick={() => setChallengeOpen(true)}
                  className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5"><Icon name="sword" size={12} /> Défier</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {chatOpen && profile && <ChatModal friend={profile} onClose={() => setChatOpen(false)} />}
      {challengeOpen && profile && <ChallengeModal friend={profile} onClose={() => setChallengeOpen(false)} />}

      {/* XP bar */}
      <XPBar xp={profile.xp ?? 0} />

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="card text-center">
          <p className="text-3xl font-black text-white">{pronostics.length}</p>
          <p className="text-xs text-zinc-500 mt-1">Pronostics</p>
        </div>
        <div className="card text-center">
          <p className="text-3xl font-black text-f1red">{profile.totalScore ?? 0}</p>
          <p className="text-xs text-zinc-500 mt-1">Points au total</p>
          {seasonDriverPoints > 0 && (
            <p className="text-[10px] text-zinc-500 mt-0.5">{pronosticScore} pronos + {seasonDriverPoints} pilote</p>
          )}
        </div>
        <div className="card text-center">
          <p className="text-3xl font-black text-white">{avg ?? '—'}</p>
          <p className="text-xs text-zinc-500 mt-1">Moyenne / course</p>
        </div>
      </div>

      {/* Season driver */}
      {driverObj && (
        <div className="card mb-6 overflow-hidden relative" style={{ borderColor: `${driverObj.color}44` }}>
          <div className="absolute inset-0 pointer-events-none"
            style={{ background: `linear-gradient(135deg, ${driverObj.color}10 0%, transparent 60%)` }} />
          <div className="flex items-center gap-4 relative z-10">
            <div className="w-16 h-16 rounded-xl overflow-hidden shrink-0" style={{ backgroundColor: `${driverObj.color}22` }}>
              <img src={driverImgUrl(driverObj.img)} alt={driverObj.name}
                className="w-full h-full object-cover object-top"
                onError={e => { e.target.style.display = 'none'; }} />
            </div>
            <div className="flex-1">
              <p className="text-[10px] font-bold uppercase tracking-widest mb-0.5" style={{ color: driverObj.color }}>Pilote de saison · {driverObj.team}</p>
              <p className="text-white font-black text-xl">{driverObj.name}</p>
              <p className="text-zinc-500 text-xs">#{driverObj.number}</p>
            </div>
            {seasonDriverPoints > 0 && (
              <span className="text-xl font-black shrink-0" style={{ color: driverObj.color }}>+{seasonDriverPoints} pts</span>
            )}
          </div>
        </div>
      )}

      {/* Pronostics */}
      <h2 className="text-lg font-bold text-white mb-4">Pronostics</h2>
      {pronostics.length === 0
        ? <div className="card text-center py-10 text-zinc-500">Aucun pronostic.</div>
        : <div className="flex flex-col gap-4">{pronostics.map(p => <PronosticCard key={p._id} pronostic={p} />)}</div>
      }
    </div>
  );
};

export default PlayerProfile;
